"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Card, CardContent } from "@/components/ui/card";
import { Loader, Mail, Phone, MapPin, Linkedin, Twitter, Facebook } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ContactFormAndDetails() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1,
    once: true
  });
  return <section ref={sectionRef} className="py-16 md:py-24 bg-background">
      <div className="container mx-auto px-4 max-w-7xl">
        <div className="grid md:grid-cols-2 gap-12 lg:gap-16">
          {}
          <div className={`${isVisible ? "animate-fade-in-up" : "opacity-0"}`}>
            <div className="mb-8">
              <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-3">
                Get in Touch
              </h2>
              <p className="text-muted-foreground text-lg">
                Ready to secure your real estate advantage? Fill out the form and our team will respond within 24 hours.
              </p>
            </div>

            <FormProvider className="bg-muted rounded-lg shadow-soft p-6 md:p-8 hover:shadow-medium transition-all duration-[var(--duration-medium)]" formId="contact-form-main" sectionName="contact-section" defaultValues={{
            name: "",
            email: "",
            phone: "",
            message: ""
          }} validationRules={{
            name: {
              required: true,
              minLength: 2,
              maxLength: 50
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: false,
              pattern: "phone"
            },
            message: {
              required: true,
              minLength: 10,
              maxLength: 1000
            }
          }} submitText="Send Message" successMessage="Thank you! We'll get back to you within 24 hours." errorMessage="Failed to submit. Please try again.">
              <ContactFormContent />
            </FormProvider>
          </div>

          {}
          <div className={`space-y-8 ${isVisible ? "animate-fade-in-up-delay-100" : "opacity-0"}`}>
            {}
            <div className="space-y-6">
              <h3 className="text-2xl font-display font-semibold text-foreground mb-6">
                Contact Information
              </h3>
              
              <div className="flex items-start gap-4 group">
                <div className="p-3 rounded-md bg-primary-light group-hover:bg-primary transition-colors duration-[var(--duration-medium)]">
                  <Phone className="size-5 text-primary group-hover:text-foreground-primary transition-colors duration-[var(--duration-medium)]" />
                </div>
                <div>
                  <p className="font-semibold text-foreground mb-1">Phone</p>
                  <p className="text-muted-foreground">+234 800 123 4567</p>
                </div>
              </div>

              <div className="flex items-start gap-4 group">
                <div className="p-3 rounded-md bg-primary-light group-hover:bg-primary transition-colors duration-[var(--duration-medium)]">
                  <Mail className="size-5 text-primary group-hover:text-foreground-primary transition-colors duration-[var(--duration-medium)]" />
                </div>
                <div>
                  <p className="font-semibold text-foreground mb-1">Email</p>
                  <p className="text-muted-foreground">info@nethill.com</p>
                </div>
              </div>

              <div className="flex items-start gap-4 group">
                <div className="p-3 rounded-md bg-primary-light group-hover:bg-primary transition-colors duration-[var(--duration-medium)]">
                  <MapPin className="size-5 text-primary group-hover:text-foreground-primary transition-colors duration-[var(--duration-medium)]" />
                </div>
                <div>
                  <p className="font-semibold text-foreground mb-1">Headquarters</p>
                  <p className="text-muted-foreground">Lagos, Nigeria</p>
                </div>
              </div>

              {}
              <div className="pt-4 border-t border-border">
                <p className="font-semibold text-foreground mb-4">Follow Us</p>
                <div className="flex gap-3">
                  {[{
                  Icon: Linkedin,
                  label: "LinkedIn"
                }, {
                  Icon: Twitter,
                  label: "Twitter"
                }, {
                  Icon: Facebook,
                  label: "Facebook"
                }].map(({
                  Icon,
                  label
                }, i) => <Button key={i} data-index={i} variant="ghost" size="icon" aria-label={label} className="p-3 rounded-md bg-primary-light text-primary hover:bg-primary hover:text-foreground-primary hover:scale-110 transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)]">
                      <Icon className="size-5" />
                    </Button>)}
                </div>
              </div>
            </div>

            {}
            <div className={`space-y-4 ${isVisible ? "animate-fade-in-up-delay-200" : "opacity-0"}`}>
              <h3 className="text-2xl font-display font-semibold text-foreground mb-6">
                Our Locations
              </h3>
              
              {[{
              city: "Lagos",
              img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1522495/img/lagos_office_map.png"
            }, {
              city: "Abuja",
              img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1522495/img/abuja_office_map.png"
            }, {
              city: "Port Harcourt",
              img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1522495/img/port_harcourt_office_map.png"
            }].map((location, i) => <Card key={i} data-index={i} className="overflow-hidden hover:-translate-y-1 hover:shadow-medium transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)] cursor-pointer bg-card border border-border">
                  <CardContent className="p-0">
                    <div className="relative h-32 bg-muted">
                      <img src={location.img} alt={`${location.city} office location`} className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-gradient-to-t from-foreground/60 to-transparent flex items-end">
                        <p className="text-white font-semibold p-4 text-lg">
                          {location.city}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>)}
            </div>
          </div>
        </div>
      </div>
    </section>;
}
function ContactFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-6 bg-green-50 border-2 border-green-200 rounded-lg text-center">
        <div className="mb-4">
          <div className="inline-flex items-center justify-center size-16 rounded-full bg-green-100 mb-3">
            <svg className="size-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
        </div>
        <p className="text-green-800 font-semibold mb-4">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-2">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className="space-y-5">
        <FormField control={control} name="name" rules={{
        required: "Name is required",
        minLength: {
          value: 2,
          message: "Name must be at least 2 characters"
        },
        maxLength: {
          value: 50,
          message: "Name must be less than 50 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-medium">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" className="focus-visible:border-primary focus-visible:scale-[1.01] transition-all duration-[var(--duration-medium)]" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-medium">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" className="focus-visible:border-primary focus-visible:scale-[1.01] transition-all duration-[var(--duration-medium)]" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        pattern: {
          value: /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}$/,
          message: "Please enter a valid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-medium">Phone Number (Optional)</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+234 800 000 0000" className="focus-visible:border-primary focus-visible:scale-[1.01] transition-all duration-[var(--duration-medium)]" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="message" rules={{
        required: "Message is required",
        minLength: {
          value: 10,
          message: "Message must be at least 10 characters"
        },
        maxLength: {
          value: 1000,
          message: "Message must be less than 1000 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-medium">Message</FormLabel>
              <FormControl>
                <Textarea placeholder="Tell us about your real estate needs or digital marketing goals..." className="min-h-32 focus-visible:border-primary focus-visible:scale-[1.01] transition-all duration-[var(--duration-medium)]" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {submitError && <div className="p-4 bg-red-50 border-2 border-red-200 rounded-lg">
            <p className="text-red-800 font-medium">{submitError}</p>
          </div>}

        <Button type="submit" disabled={isSubmitting} className="w-full btn-primary h-12 text-base font-semibold">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-5" />
              Sending...
            </> : 'Send Message'}
        </Button>
      </div>
    </Form>;
}